/**
 * Play-run timeline (W1-14): a piecewise-linear map from AudioContext time to music position (A§8:
 * `musicPos = startPos + (ctxTime - startCtx) * rate`). One segment per sample-accurate play run of the music:
 * a play/seek starts one, a rate change splices one in at a future context time (continuous position), and a loop
 * wrap appends one that starts exactly where the previous one ends. The scheduler converts emission times to
 * `start(when)` times through the same segments, so audio, hitsounds and the visual clock always agree.
 *
 * Occurrences: every segment carries its play `run` and loop `lap`. A play start or a seek begins a new run; rate
 * splices, offset changes, path switches and media-element re-anchors continue the run and the lap; a loop wrap
 * starts the next lap. An emission plays at most once per (run, lap), however the mapping moves while it rings.
 *
 * Positions are MUSIC seconds; chart seconds are `musicPos - shift` (shift = offset + info offset + preview
 * audio delay, R§6.1), clamped at 0 only for display.
 */

export interface Segment {
  /** Unique, increasing id. */
  readonly id: number;
  /** Play run: a play start or a seek begins a new one; everything else continues it. */
  readonly run: number;
  /** Loop iteration within the run (0 until the first wrap, +1 at every wrap). */
  readonly lap: number;
  /**
   * Context time at which `posStart` plays. +Infinity while the segment is provisional (media-element path before
   * its first clock sample): the position then holds at `posStart` and nothing is scheduled for it.
   */
  readonly ctxStart: number;
  /** Music position at `ctxStart` (may be negative: silence before the music when the chart starts earlier). */
  readonly posStart: number;
  /** Playback rate (music seconds per context second). */
  readonly rate: number;
  /** Context time at which the segment stops (+Infinity while open-ended). */
  ctxEnd: number;
}

export function isProvisional(seg: Segment): boolean {
  return seg.ctxStart === Infinity;
}

/** Music position of `seg` at context time `t` (holds at the start before `ctxStart`, and at the end after `ctxEnd`). */
export function segmentPosAt(seg: Segment, t: number): number {
  if (!(t > seg.ctxStart)) return seg.posStart;
  const tt = Math.min(t, seg.ctxEnd);
  return seg.posStart + (tt - seg.ctxStart) * seg.rate;
}

/** Context time at which `seg` plays music position `pos` (unclamped; may lie outside the segment). */
export function segmentCtxAt(seg: Segment, pos: number): number {
  return seg.ctxStart + (pos - seg.posStart) / seg.rate;
}

/**
 * The segment that is sounding at context time `t`: the last one whose start is at or before `t`, or the first
 * one while `t` is before every start (lead-in hold). Undefined for an empty list.
 */
export function segmentAt(segs: readonly Segment[], t: number): Segment | undefined {
  let found: Segment | undefined;
  for (const s of segs) {
    if (s.ctxStart <= t) found = s;
    else break;
  }
  return found ?? segs[0];
}

/** Music position at context time `t` across the segment list (`fallback` when the list is empty). */
export function timelinePosAt(segs: readonly Segment[], t: number, fallback: number): number {
  const seg = segmentAt(segs, t);
  return seg === undefined ? fallback : segmentPosAt(seg, t);
}
